import { ComponentObject } from './models/componet.model';
import { ComponentListService } from './services/component-list.service';
import { Renderer2, RendererFactory2, Injector } from '@angular/core';
import { fromEvent } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PageLayoutDialog } from './dialogs/page-layout-dialog.component';
import { PershingTranslationService } from '@pershing/pershing-core';
import { merge } from 'lodash';
import { EditConstants, EditEvent, SiteBuilderActions } from './constants/edit.constants';
import { PageStateService } from './services/page-state.service';

export class EditLoader {
  private renderer: Renderer2;
  private modes: string[] = ['ed-Component']; // defaulting to this for now
  private bodyModes: string[];
  private translationService: PershingTranslationService;
  private pageEditState: PageStateService;
  private componentListService: ComponentListService;
  private isEditMode: boolean = false;
  private isSitebuilder: boolean = false;
  private componentMeta: any = {};
  private pageMeta: any = {};
  private appSettings: any;

  constructor(public injector: Injector) {
    let rendererFactory = this.injector.get(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);
    this.translationService = this.injector.get(PershingTranslationService);
    this.pageEditState = this.injector.get(PageStateService);
    this.componentListService = this.injector.get(ComponentListService);
    this.injector.get(ComponentListService);
  }

  public subscribe(): void {
    fromEvent(document, EditEvent.CONTENT_PLACED)
      .subscribe(x => {
        this.addEditComponents();
        // tell parent page (i.e. sitebuilder) that the page has completed loading
        this.emitPageLoadEvent();
      });
    fromEvent(window, EditEvent.LOAD_EDIT_MODE)
      .subscribe((evt: any) => {
        this.isEditMode = true;
        this.addEditProperties(evt.detail);
        this.addEditComponents();
      });
    fromEvent(window, EditEvent.STOP_EDIT_MODE)
      .subscribe(x => {
        console.log('STOP');
        this.isEditMode = false;
        this.removeEditComponents();
        // refresh window
      });
    fromEvent(window, EditEvent.LAYOUT_SELECTION_DIALOG)
      .subscribe(evt => {
        this.changeLayoutDialog(evt);
      });
    fromEvent(window, EditEvent.ADD_COMPONENT)
      .subscribe(evt => {
        this.addComponentListener(evt);
      });
    fromEvent(window, EditEvent.REMOVE_COMPONENT)
      .subscribe(evt => {
        this.removeComponentListener(evt, false);
      });
    fromEvent(window, EditEvent.RELOCATE_COMPONENT)
      .subscribe(evt => {
        this.relocateComponentListener(evt);
      });
    if (this.isEditMode) {
      window.addEventListener('message', this.onMessage.bind(this), false);
    }
  }

  onMessage($event) {
    if ($event.data.action === SiteBuilderActions.EDIT_PROPERTY) {
      this.updateProperty($event.data);
    }
    if ($event.data.action === SiteBuilderActions.EDIT_STYLE) {
      this.addStyleElement($event.data.text);
    }
    if ($event.data.action === SiteBuilderActions.REMOVE_STYLE) {
      this.removeStyleElement($event.data);
    }
    if ($event.data.action === SiteBuilderActions.EDIT_LAYOUT) {
      this.emitChangeLayoutEvent($event.data.pageId, $event.data.layout);
    }
    if ($event.data.action === SiteBuilderActions.PAGE_CSS) {
      this.pageMeta = $event.data;
      let data = {
        action: SiteBuilderActions.DEFAULT_CSS,
        pageId: this.pageMeta.id,
        pageRef: document.getElementById(this.pageMeta.id),
        pageCSS: window.getComputedStyle(document.getElementById(this.pageMeta.id))
      };
      console.log(data.pageCSS);
      window.parent.postMessage(JSON.parse(JSON.stringify(data)), '*');
    }
    if ($event.data.action === SiteBuilderActions.UPDATE_EDIT_ACTIONS) {
      // emit event to edit components to have correct actions
      this.emitUpdateEditActionsEvent($event.data);
    }
    if ($event.data.action === SiteBuilderActions.CHANGE_DEVICE_TYPE) {
      // emit event to edit components to have correct actions
      this.changeDeviceType($event.data);
    }
    if ($event.data.action === SiteBuilderActions.UPDATE_MODES) {
      this.resetBodyStyle();
      this.addEditProperties($event.data);
      this.addEditComponents();
    }
  }
  private addStyleElement(input) {
    let node = document.createElement('style');
    node.innerHTML = input;
    document.body.appendChild(node);
  }
  private removeStyleElement(input) {
    const styleTags = <HTMLScriptElement[]><any>document.body.getElementsByTagName('style');
    let keyArray: string[] = input.key.split('.');
    for (let index = 0; index < styleTags.length; index++) {
      let node = styleTags[index];
      if (node.innerHTML.includes(keyArray[0]) && node.innerHTML.includes(keyArray[1])) {
        node.remove();
      }
    }
  }

  // private updateWindow() {
  //   let resizeEvent = document.createEvent('HTMLEvents');
  //   resizeEvent.initEvent('resize', true, false);
  //   window.dispatchEvent(resizeEvent);
  // }

  private resetBodyStyle() {
    const body = document.querySelector('body');
    if (this.bodyModes && this.bodyModes.length > 0) {
      this.bodyModes.forEach(modeName => {
        this.renderer.removeClass(body, modeName);
      });
    }
  }

  addEditProperties(data): void {
    if (data) {
      this.modes = (data.modes) ? data.modes : this.modes;
      this.bodyModes = (data.bodyModes) ? data.bodyModes : this.bodyModes;
      this.pageMeta = (data.pageMeta) ? data.pageMeta : this.pageMeta;
      this.componentMeta = (data.componentMeta) ? data.componentMeta : this.componentMeta;
      if (this.componentMeta) {
        this.pageEditState.setComponentList(this.componentMeta.componentList);
      }
    }
  }

  removeEditComponents() {
    const components = <HTMLScriptElement[]><any>document.getElementsByClassName(EditConstants.TAG);
    if (components) {
      for (let index = 0, len = components.length; index < len; index++) {
        if (components[index] && components[index].parentNode) {
          this.renderer.removeChild(this.renderer.parentNode(components[index]), components[index]);
        }
      }
    }
  }

  public addEditComponents(): void {
    if (this.isEditMode) {
      const body = document.querySelector('body');
      if (this.bodyModes && this.bodyModes.length > 0) {
        this.bodyModes.forEach(modeName => {
          this.renderer.addClass(body, modeName);
        });
      }
      // If the page id is passed in for editing, then only that page should be edited.
      let columns; 
      if (this.pageMeta && this.pageMeta.id) {
        let editablePage = <HTMLElement><any>document.getElementById(this.pageMeta.id);
        columns = <HTMLScriptElement[]><any>editablePage.getElementsByClassName('column-finder');
      } else {
        // Handle the case where the page metadata is not passed in, thus all pages are editable.
        columns = <HTMLScriptElement[]><any>document.getElementsByClassName('column-finder');
      }
      if (columns) {
        for (let index = 0; index < columns.length; index++) {
          let currentColumn = columns[index];
          if (currentColumn) {
            let pageId = this.resolvePageId(currentColumn);
            let addComponent = this.generateAddComponent(currentColumn.dataset.id, pageId, true);
            if (currentColumn.firstElementChild) {
              if (!currentColumn.firstElementChild.classList.contains(currentColumn.dataset.id)) {
                this.renderer.insertBefore(currentColumn, addComponent, currentColumn.firstElementChild);
              }
            } else {
              this.renderer.appendChild(currentColumn, addComponent);
            }
            this.addEditComponentsOnColumn(currentColumn, pageId);
          }
        }
        this.publishComponentUpdate();
      }
    }
  }

  private addEditComponentsOnColumn(column: HTMLScriptElement, pageId: string): void {
    if (this.isEditMode) {
      const components = <HTMLScriptElement[]><any>column.getElementsByClassName('finder');
      if (components) {
        for (let index = 0; index < components.length; index++) {
          const currentComponent = components[index];
          // console.log('current component: ', currentComponent);
          if (currentComponent) {
            let parentNode: HTMLScriptElement = this.renderer.parentNode(currentComponent);
            // if it's the first child in the list, then only add the edit component, but only if it's not already there
            if (index === 0) {
              let firstIndexLocation = currentComponent.dataset.id;
              if (!currentComponent.lastElementChild || !currentComponent.lastElementChild.classList.contains(firstIndexLocation)) {
                this.renderer.appendChild(currentComponent, this.generateEditComponent(firstIndexLocation, currentComponent, pageId));
                this.applyCss(currentComponent);
              }
            } else {
              let location = `${currentComponent.dataset.id}${EditConstants.DELIMITER}0`;
              if (!currentComponent.previousElementSibling
                      || !currentComponent.previousElementSibling.classList.contains(location)) {
                  // Only add the add component and edit component if they are not already added to the page.
                this.addComponents(currentComponent, parentNode, pageId);
                this.applyCss(currentComponent);
              }
            }
            // if it's the last child in the list, also add the add component below, but only if it's not already there
            if (index === components.length - 1) {
              let lastIndexLocation = `${currentComponent.dataset.id}${EditConstants.DELIMITER}1`;
              if (!currentComponent.nextElementSibling || !currentComponent.nextElementSibling.classList.contains(lastIndexLocation)) {
                this.renderer.appendChild(column, this.generateAddComponent(lastIndexLocation, pageId));
              }
            }
          }
        }
      }
    }
  }

  private resolvePageId(element: HTMLElement): string {
    if (element.tagName.toLocaleLowerCase() === 'dynamic-component') {
      return element.firstElementChild.id;
    } else if (element.parentElement) {
      return this.resolvePageId(element.parentElement);
    } else {
      return '';
    }
  }

  private addComponents(currentComponent: HTMLScriptElement, parentNode: HTMLScriptElement, pageId: string, specifiedTag: string = '') {
    this.renderer.appendChild(currentComponent, this.generateEditComponent(`${currentComponent.dataset.id}`, currentComponent, pageId, specifiedTag));
    this.renderer.insertBefore(parentNode, this.generateAddComponent(`${currentComponent.dataset.id}${EditConstants.DELIMITER}0`, pageId), currentComponent);
  }

  private applyCss(currentComponent: HTMLScriptElement) {
    if (this.modes && this.modes.length > 0) {
      this.modes.forEach(modeName => {
        this.renderer.addClass(currentComponent, modeName);
      });
    }
  }

  private generateAddComponent(location: string, pageId: string, isColumn: boolean = false): HTMLScriptElement {
    let surroundingDiv: HTMLScriptElement = this.renderer.createElement('div');
    this.renderer.addClass(surroundingDiv, location);
    this.renderer.addClass(surroundingDiv, EditConstants.TAG);
    let model = {
      location: location,
      pageId: pageId,
      isColumn: isColumn
    };
    let pershingComponentElement = this.generatePershingComponent(EditConstants.EDIT_COMPONENT.ADD, location, JSON.stringify(model));
    this.renderer.addClass(pershingComponentElement, 'ed-AddComponent');
    this.renderer.appendChild(surroundingDiv, pershingComponentElement);
    return surroundingDiv;
  }

  private generateEditComponent(location: string, currentComponent: HTMLScriptElement, pageId: string, specifiedTag: string = ''): HTMLScriptElement {
    let componentTag: string;
    Array.from(currentComponent.children).forEach(ele => {
      componentTag = ele.tagName.toLocaleLowerCase();
      if (componentTag === 'pershing-comp') {
        componentTag = specifiedTag;
      }
    });
    let compId = currentComponent.dataset.id;
    let model = {
      componentTag: componentTag,
      location: location,
      componentId: compId,
      pageId: pageId
    };
    let pershingComponentElement = this.generatePershingComponent(EditConstants.EDIT_COMPONENT.EDIT, location, JSON.stringify(model));
    this.renderer.addClass(pershingComponentElement, EditConstants.TAG);
    return pershingComponentElement;
  }

  public addComponentListener (evt) {
    const newComponentEvent = evt.detail.data;
    const component = newComponentEvent.component;
    const position = newComponentEvent.newLocation.split(EditConstants.DELIMITER)[1];
    const componentLocation = <HTMLScriptElement[]><any>document.getElementsByClassName(newComponentEvent.newLocation)[0];
    const parentLocation: HTMLScriptElement = this.renderer.parentNode(componentLocation);
    const newComponentId = newComponentEvent.id;
    let surroundingDiv: HTMLScriptElement = newComponentEvent.relocationComponent;
    if (!surroundingDiv) {
      surroundingDiv = this.renderer.createElement('div');
      this.applyCss(surroundingDiv);
      this.renderer.addClass(surroundingDiv, 'finder');
      this.renderer.addClass(surroundingDiv, newComponentId);
      this.renderer.setAttribute(surroundingDiv, 'data-id', newComponentId);
      this.renderer.setAttribute(surroundingDiv, 'id', `id${newComponentId}`);
      this.renderer.setAttribute(surroundingDiv, 'data-business-id', newComponentEvent.component.businessComponentId);
      this.renderer.setAttribute(surroundingDiv, 'component-id', component.componentSelector);

      const compMetadata = {
        package: component.componentModule.split('#')[0],
        moduleName: component.componentModule.split('#')[1],
        selector: component.selector
      };

      // Add component to list of page component (to inhibit duplicate instantiations)
      this.pageEditState.addComponentToList(component);

      let pershingComponentElement = this.generatePershingComponent(compMetadata, newComponentId, this.createDataModel(component), newComponentId, true);
      // Create the same div structure for the inserted components.
      this.renderer.appendChild(surroundingDiv, pershingComponentElement);
    }
    // Add the new component to the dom before the add component location
    this.renderer.insertBefore(parentLocation, surroundingDiv, componentLocation);
    let pageId = this.resolvePageId(parentLocation);
    this.addComponents(surroundingDiv, parentLocation, pageId);
    if (position === '1') {
      // If this component was added to the end of the list, remove the previous add location identifier, and set it to this comp's id.
      this.renderer.removeChild(parentLocation, componentLocation);
      this.renderer.appendChild(parentLocation, this.generateAddComponent(`${newComponentId}${EditConstants.DELIMITER}1`, pageId));
      this.renderer.addClass(surroundingDiv, 'padding-top-20');
    } else {
      this.renderer.addClass(surroundingDiv, 'padding-bottom-20');
    }

    this.publishComponentUpdate();
  }

  generatePershingComponent(componentMetadata: any, location: string, model?: any, id?: string, wrapper?: boolean): any {
    let pershingComponentElement = this.renderer.createElement('pershing-comp');
    if (componentMetadata) {
        this.renderer.setAttribute(pershingComponentElement, 'data-module', componentMetadata.package + '#' + componentMetadata.moduleName);
        this.renderer.setAttribute(pershingComponentElement, 'data-component', componentMetadata.selector); // or class name
        this.renderer.setAttribute(pershingComponentElement, 'data-index', location);
        this.renderer.setAttribute(pershingComponentElement, 'data-model', model);
        if (wrapper) {
          this.renderer.setAttribute(pershingComponentElement, 'data-wrapper', String(wrapper));
        }
        this.renderer.setAttribute(pershingComponentElement, 'data-id', id);
    }
    return pershingComponentElement;
  }

  public removeComponentListener(evt, isRelocate) {
    const removeComponentEvent = evt.detail.data;
    const componentToRemove = <HTMLScriptElement><any>document.getElementById(`id${removeComponentEvent.previousLocation}`);
    const excessAddBeforeComponentList = <HTMLScriptElement[]><any>document.getElementsByClassName(`${removeComponentEvent.previousLocation}${EditConstants.DELIMITER}0`);
    const excessAddAfterComponentList = <HTMLScriptElement[]><any>document.getElementsByClassName(`${removeComponentEvent.previousLocation}${EditConstants.DELIMITER}1`);

    // const surroundingDiv = <HTMLScriptElement><any>document.getElementById(`id${removeComponentEvent.previousLocation}`).parentNode;
    // const parentDiv = <HTMLScriptElement><any>document.getElementById(`id${removeComponentEvent.previousLocation}`).parentNode.parentNode;
    // const nextelement = <HTMLScriptElement><any>document.getElementById(`id${removeComponentEvent.previousLocation}`).parentNode.parentNode.nextSibling;

    const parent = this.renderer.parentNode(componentToRemove);
    this.renderer.removeChild(parent, componentToRemove);
    if (excessAddBeforeComponentList && excessAddBeforeComponentList.length > 0) {
      this.renderer.removeChild(parent, excessAddBeforeComponentList[0]);
    }
    if (excessAddAfterComponentList && excessAddAfterComponentList.length > 0) {
      this.renderer.removeChild(parent, excessAddAfterComponentList[0]);
    }
    // this.renderer.removeChild(parent, surroundingDiv);
    // this.renderer.removeChild(parent, parentDiv);
    // if (nextelement && !parentDiv.previousSibling) {
    //   this.renderer.setStyle(nextelement, 'padding-top', '0px');
    // }
    if (!isRelocate) {
      this.pageEditState.removeComponentFromList(componentToRemove.dataset.businessId);
    }
    this.publishComponentUpdate();
  }

  public refreshComponentListener(componentId, model, pageId) {
    const componentToRemove = <HTMLScriptElement><any>document.getElementById(`id${componentId}`);
    const parent = this.renderer.parentNode(componentToRemove);

    // Get metadata necessary to render component
    let businessCompId = componentToRemove.attributes.getNamedItem('data-business-id').nodeValue;
    let componentSelector = componentToRemove.attributes.getNamedItem('component-id').nodeValue;
    let componentMeta: ComponentObject = this.resolveComponentMetadata(businessCompId, componentSelector);

    const compMetadata = {
      package: componentMeta.componentModule.split('#')[0],
      moduleName: componentMeta.componentModule.split('#')[1],
      selector: componentMeta.componentSelector
    };

    // Create new component with existing data
    let generatedComponent = this.generatePershingComponent(compMetadata, componentId, this.createDataModel(model), componentId);

    let surroundingDiv = this.renderer.createElement('div');
    this.applyCss(surroundingDiv);
    this.renderer.addClass(surroundingDiv, 'finder');
    this.renderer.addClass(surroundingDiv, `id${componentId}`);

    this.renderer.setAttribute(surroundingDiv, 'data-id', componentId);
    this.renderer.setAttribute(surroundingDiv, 'id', `id${componentId}`);
    this.renderer.setAttribute(surroundingDiv, 'component-id', componentMeta.componentSelector);
    this.renderer.setAttribute(surroundingDiv, 'data-business-id', '');

    // Create the same div structure for the inserted components.
    this.renderer.appendChild(surroundingDiv, generatedComponent);

    this.renderer.appendChild(parent, surroundingDiv);
    this.renderer.removeChild(parent, componentToRemove);

    this.addEditComponents();

    window.dispatchEvent(new CustomEvent(EditEvent.UPDATE_COMPONENT_INPUT, {
      detail: { componentId, model, pageId }
    }));
  }

  public resolveComponentMetadata(businessComponentId: string, componentId: string) {
    let selectedComponent: ComponentObject;
    if (businessComponentId) {
      // First try to resolve based off of businessComponentId
      selectedComponent = this.componentListService.fetchComponentByBusinessComponentId(businessComponentId);
    }
    if (!selectedComponent && componentId) {
      // First try to resolve based off of componentId
      selectedComponent = this.componentListService.fetchComponentBySelector(componentId);
    }
    return selectedComponent;
  }

  public changeLayoutDialog (evt) {
    console.log(evt);
    const pageIdClosure = evt.detail.pageId ? evt.detail.pageId : this.pageMeta.id;
    let dialog = this.injector.get(MatDialog);
    let dialogRef = dialog.open(PageLayoutDialog, {
      width: '950px',
      data: null
    });
    dialogRef.afterClosed().subscribe(result => {
      if ( result.confirm === true ) {
        this.emitChangeLayoutEvent(pageIdClosure, result.deviceConfiguration);
      }
    });
  }

  private emitChangeLayoutEvent(pageId: string, layout ) {
    window.dispatchEvent(new CustomEvent(EditEvent.CHANGE_LAYOUT, {
      detail: { pageId: pageId, layout: layout }
    }));
  }

  private emitUpdateEditActionsEvent(data) {
    setTimeout(() => {  // This is to fix race condition, event getting passed before edit-components getting rendererd
      window.dispatchEvent(new CustomEvent(SiteBuilderActions.UPDATE_EDIT_ACTIONS, {detail: data}));
    }, 1500);
  }

  private emitPageLoadEvent() {
    let data = {
      action: EditEvent.CONTENT_PLACED
    };
    window.parent.postMessage( JSON.parse(JSON.stringify(data)), '*');
  }

  private changeDeviceType(data) {
    if ((<any>window).loadcssfile !== undefined) {
      if (data.deviceType && this.appSettings.styleConfig && this.appSettings.styleConfig[data.deviceType]) {
        // Remove Existing Stylesheet
        let existingStyle: HTMLElement = document.getElementById('deviceStyle');
        if (existingStyle && existingStyle.parentElement) {
          this.renderer.removeChild(existingStyle.parentElement, existingStyle);
        }
        // Load Device Stylesheet
        (<any>window).loadcssfile(this.appSettings.styleConfig[data.deviceType], 'deviceStyle');
        // Trigger Page Refresh with DeviceType
        window.dispatchEvent(new CustomEvent(EditEvent.TRIGGER_PAGE_REFRESH, {detail: data}));
      } else {
        console.log('Unable to change deviceType');
      }
    }
  }

  public createDataModel(model): string {
    let compData = merge(this.componentMeta, model);
    compData = merge(compData, model.input);
    // compData.componentMeta = cloneDeep(this.componentMeta);
    return JSON.stringify(compData);
  }

  public relocateComponentListener(evt) {
    const relocateComponentEvent = evt.detail.data;
    let relocatingComponent = <HTMLScriptElement><any>document.getElementById(`id${relocateComponentEvent.previousLocation}`);
    evt.detail.data.relocationComponent = relocatingComponent;
    this.removeComponentListener(evt, true);
    this.addComponentListener(evt);
  }

  public updateProperty(evt) {
    if (evt.isDomProperty) {
      // Event handing for angular inputs (DomProperties)
      let modelDelta = {};
      modelDelta[evt.key] = evt.value;
      this.refreshComponentListener(evt.componentId, modelDelta, evt.pageId);
    } else {
      // Event handling for common properties
      this.translationService.addTranslationOverride('en', evt.key, evt.value);
    }
  }

  private publishComponentUpdate(): void {
    window.dispatchEvent(new CustomEvent('componentUpdate', {
      detail: { updated: true }
    }));
  }

  private addSiteOverlay() {
    let overlay: HTMLScriptElement = this.renderer.createElement('div');
    this.renderer.addClass(overlay, 'ed-Overlay_NullClick');
    this.renderer.appendChild(document.body, overlay);
  }

  private addSBFeatures(): void {
    if (this.isSitebuilder) {
      this.addSiteOverlay();
    }
  }

  public initialize(appSettings: any): void {
    // Check if token has been already set in AppSettings
    if (appSettings) {
      this.appSettings = appSettings;
      if (appSettings.isEditMode) {
        this.isEditMode = appSettings.isEditMode;
        // This is needed for sitebuilder specific features
        this.isSitebuilder = true;
        // if (!window.sessionStorage.getItem('sitebuilderAccess') || window.sessionStorage.getItem('sitebuilderAccess') !== 'true') {
        //     this.isSitebuilder = true;
        // }
      }
      if (appSettings.modes) {
        this.modes = appSettings.modes;
      }
    }
    this.addEditComponents();
    this.addSBFeatures();
    this.subscribe();
  }
}
